/**
 * ErrorState — red-palette error card (Phase 1).
 *
 * Pattern locked in heymotek-web preview /preview/dashboard/states/:
 *   border-red-200, text-red-700 heading, calm muted body,
 *   border-red-300 outline "Try again" button (+ optional neutral
 *   "Refresh page" outline button as secondary).
 *
 * Matches the Account "Danger zone" palette per brand sheet — red here
 * means "something failed, here's the recovery path", consistent with
 * the danger zone's "irreversible if you proceed" meaning. Distinct from
 * coral (action / brand identity) and slate (ambient).
 *
 * Use this for per-page fetch errors. The top-level ErrorBoundary in
 * AppShell catches render crashes with a similar but more generic
 * fallback.
 */

import { RefreshCw, AlertCircle } from 'lucide-react';

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  /** Show the secondary "Refresh page" button (window.location.reload). */
  showRefresh?: boolean;
  className?: string;
}

export function ErrorState({
  title = "Couldn't load this page",
  description = 'Something went wrong on our end. Your data is safe — try again, or refresh if it persists.',
  onRetry,
  showRefresh = true,
  className,
}: ErrorStateProps) {
  return (
    <section
      className={`rounded-3xl border border-red-200 bg-white p-10 text-center sm:p-14 ${className ?? ''}`}
    >
      <AlertCircle
        className="mx-auto mb-4 h-12 w-12 text-red-700"
        strokeWidth={1.5}
      />
      <h3 className="mb-2 text-xl font-extrabold text-red-700 sm:text-2xl">
        {title}
      </h3>
      <p className="mx-auto mb-6 max-w-md text-base text-muted-foreground">
        {description}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
        )}
        {showRefresh && (
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg border border-dark/15 bg-white px-4 py-2 text-sm font-semibold text-dark transition-colors hover:border-dark/30"
          >
            Refresh page
          </button>
        )}
      </div>
    </section>
  );
}
